import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';
import { cn } from '@/lib/utils';
import { storageKeys } from '@/lib/env';
import { RouteProgress } from '@/components/ui/RouteProgress';
import { Skeleton } from '@/components/ui/Feedback';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

/** Route handles supply the page title, so the topbar has no route table of its own. */
export interface RouteHandle {
  title: string;
  subtitle?: string;
  /** Show the topbar date filter — only on screens whose data is date-scoped. */
  dateFilter?: boolean;
}

export function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(storageKeys.sidebar) === 'true',
  );
  const location = useLocation();
  const matches = useMatches();

  const handle = [...matches].reverse().find((match) => (match.handle as RouteHandle)?.title)
    ?.handle as RouteHandle | undefined;

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  useEffect(() => {
    localStorage.setItem(storageKeys.sidebar, String(collapsed));
  }, [collapsed]);

  return (
    <div className="min-h-dvh">
      <RouteProgress />
      <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} collapsed={collapsed} />

      <div className={cn('flex min-h-dvh flex-col transition-[padding]', collapsed ? 'lg:pl-[76px]' : 'lg:pl-[268px]')}>
        <Topbar
          title={handle?.title ?? 'Dashboard'}
          subtitle={handle?.subtitle}
          showDateFilter={handle?.dateFilter ?? false}
          onMenuClick={() => setDrawerOpen(true)}
          onCollapseToggle={() => setCollapsed((value) => !value)}
        />

        <main className="flex-1 px-4 py-5 lg:px-7 lg:py-6">
          {/* Every feature screen is a separate chunk. Without a boundary here the
              first paint of a route is a blank main area; with one it is the shape
              of the page that is about to arrive. */}
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        </main>

        <footer className="px-4 pb-6 text-center text-xs text-ink-muted lg:px-7">
          {new Date().getFullYear()} © Presented by <span className="font-bold text-ink-soft">Suffix IT Limited</span>
        </footer>
      </div>
    </div>
  );
}

/**
 * Generic page silhouette: a toolbar, a row of tiles, a wide panel.
 *
 * Deliberately neutral — it stands in for whichever screen is loading, and is on
 * screen for the length of a chunk fetch, so matching any one page exactly would
 * buy nothing.
 */
function PageFallback() {
  return (
    <div className="flex flex-col gap-5" role="status" aria-label="Loading page">
      <div className="flex items-center justify-between gap-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-9 w-32 rounded-xl" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="card flex flex-col gap-3 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="size-10 shrink-0 rounded-xl" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-6 w-16" />
              </div>
            </div>
            <Skeleton className="h-3.5 w-28" />
          </div>
        ))}
      </div>

      <div className="card p-5">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="mt-4 h-[260px] w-full rounded-xl" />
      </div>
    </div>
  );
}
